import type { Container } from 'pixi.js';
import type { BoosterTypeController } from './types';
import type { GameAreaController } from '../GameAreaController';
import type { TileStackModel } from '../../models/board/TileStackModel';
import type { TileStackView } from '../../views/board/TileStackView';
import { BoosterHammerModel } from '../../models/boosters/BoosterHammerModel';
import { BoosterHammerView } from '../../views/boosters/BoosterHammerView';
import type { GameTutorialController } from '../../../tutorials/game/GameTutorialController';
import { TutorialHand } from '../../../views/common/TutorialHand';

export class BoosterHammerController implements BoosterTypeController {
  private readonly boosterModel: BoosterHammerModel;

  public readonly isInstant = false;

  private tutorial: GameTutorialController | null = null;

  private readonly tutorialHands: Container[] = [];

  #cancel?: () => void;

  constructor(private readonly gameAreaController: GameAreaController) {
    this.boosterModel = new BoosterHammerModel(gameAreaController.boardModel);
  }

  public canExecute(): boolean {
    return this.boosterModel.canExecute();
  }

  private getStacksToHighlight(): TileStackView[] {
    if (!this.tutorial) return [];

    const stacksToHighlight = this.tutorial.getAvailableStacksForBooster();
    return stacksToHighlight
      .map((point) => {
        const stack = this.gameAreaController.boardModel.getStack(point.x, point.y);
        if (!stack) return null;
        const stackView = this.gameAreaController.getBoardView().getStackView(stack.id);
        if (!stackView || stackView.destroyed) return null;

        return stackView;
      })
      .filter(Boolean) as TileStackView[];
  }

  private addHighlightToStacks(): void {
    this.getStacksToHighlight().forEach((view) => {
      view.highlight.add();

      this.tutorialHands.push(
        new TutorialHand()
          .props({
            parent: this.gameAreaController.getBoardView(),
            x: view.x,
            y: view.y + view.getNextTileZOffset(),
          })
          .setClicking(),
      );
    });
  }

  private removeHighlightFromStacks(): void {
    this.getStacksToHighlight().forEach((view) => {
      view.highlight.remove();
    });
  }

  public execute(onComplete: (success: boolean) => void, tutorial: GameTutorialController | null): this {
    const {
      view: { boardView },
    } = this.gameAreaController;

    this.tutorial = tutorial;
    this.boosterModel.setTutorialController(tutorial);

    this.addHighlightToStacks();

    const selectTileStack = async (stack: TileStackModel, stackView: TileStackView): Promise<void> => {
      if (!this.boosterModel.canExecuteOnStack(stack)) return;

      this.cleanUp();

      boardView.onStackPointerDown.disconnect(selectTileStack);

      await new BoosterHammerView().play(boardView, stackView);
      this.boosterModel.execute(stack);
      onComplete(true);
    };

    boardView.onStackPointerDown.connect(selectTileStack);

    this.#cancel = (): void => {
      this.cleanUp();
      boardView.onStackPointerDown.disconnect(selectTileStack);
    };

    return this;
  }

  private cleanUp(): void {
    this.removeHighlightFromStacks();
    this.tutorialHands.forEach((hand) => {
      hand.destroy();
    });
  }

  public cancel(): void {
    this.#cancel?.();
  }
}
